/*
  Shopify Forms indsætter et element i nederste højre hjørne med
  z-index 2147483647, den højest mulige værdi. Elementet bor i appens egen
  shadow root, så vi kan hverken lægge os oven på det eller style det. Uden
  denne fil dækker det temaets faste købsknap fuldstændigt: elementFromPoint
  på knappens midte returnerer appen i stedet for knappen.

  Vi måler derfor appens fane og reserverer præcis den plads, der skal til,
  under bjælkens indhold. Bjælkens baggrund bliver, hvor den er, så fanen
  lander inde i bjælken i stedet for oven på knappen.

  To ting er vigtige i målingen:

  1. Bjælken glider ind med en transform-animation over 0,3 sekunder.
     getBoundingClientRect() returnerer den animerede position, ikke
     hvilepositionen, og en måling midt i animationen gav 198 px, hvor det
     rigtige tal var 84. Derfor beregnes bjælkens underkant ud fra dens
     bottom-værdi i stedet, som er uafhængig af animationen.
  2. Fanen har ikke samme størrelse overalt. Målt: 208 x 84 px ved 390 px
     bredde, 239 x 116 px ved 800 og 1024 px. Et fast tal er derfor forkert.
*/

const BAR_SELECTOR = '.sticky-add-to-cart__bar';
const CLEARANCE_PROPERTY = '--sticky-add-to-cart-embed-clearance';

/*
  Kan vi slet ikke læse appens shadow root, reserverer vi den største
  størrelse, vi har målt. Det koster lidt spildt plads, men holder knappen
  trykbar. En knap, der ikke kan trykkes på, er en dyrere fejl end en bjælke,
  der fylder for meget.

  Det sker kun, hvis appen skifter sin shadow root til mode: closed. Så længe
  vi kan læse den, måler vi og gætter ikke.
*/
const FALLBACK_CLEARANCE = 116;

/*
  Hvor tæt på bjælkens underkant et element skal nå ned for at kunne dække
  knappen. Appens fane går helt til skærmkanten, så der skal ikke meget slæk
  til. Slækket findes kun, så desktoppens bottom: 20px ikke sorterer fanen fra.
*/
const BOTTOM_TOLERANCE = 40;

// Hvor længe vi bliver ved med at lede efter en læsbar shadow root: 20 gange 500 ms.
const RETRY_INTERVAL = 500;
const RETRY_LIMIT = 20;

let active = null;

function start() {
  const bar = document.querySelector(BAR_SELECTOR);
  if (!bar) return null;

  let alive = true;
  let written = null;
  let observedTeaser = null;
  let observedShadowRoot = null;
  let shadowObserver = null;
  let frame = null;
  let retryTimer = null;
  let retriesLeft = RETRY_LIMIT;

  const teaserObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);

  function write(clearance) {
    if (clearance === written) return;
    written = clearance;

    if (clearance === null) {
      bar.style.removeProperty(CLEARANCE_PROPERTY);
      return;
    }

    bar.style.setProperty(CLEARANCE_PROPERTY, `${clearance}px`);
  }

  /*
    Bjælken er altid position: fixed med en bottom-værdi, 0 på mobil og 20 px
    på desktop. Hvilepositionens underkant kan derfor beregnes uden at spørge
    om den animerede boks.
  */
  function restingBottom() {
    const bottom = Number.parseFloat(getComputedStyle(bar).bottom);
    return window.innerHeight - (Number.isFinite(bottom) ? bottom : 0);
  }

  function overlapsHorizontally(first, second) {
    return first.left < second.right && first.right > second.left;
  }

  function coversViewport(element) {
    const box = element.getBoundingClientRect();
    const tolerance = 2;

    return (
      box.width > 0 &&
      box.height > 0 &&
      box.left <= tolerance &&
      box.top <= tolerance &&
      box.right >= window.innerWidth - tolerance &&
      box.bottom >= window.innerHeight - tolerance
    );
  }

  function watchTeaser(teaser) {
    if (teaser === observedTeaser) return;
    if (observedTeaser && teaserObserver) teaserObserver.unobserve(observedTeaser);
    observedTeaser = teaser;
    if (teaser && teaserObserver) teaserObserver.observe(teaser);
  }

  /*
    Der findes ingen begivenhed for "shadow rooten blev læsbar". At oprette en
    shadow root udløser hverken en attribut- eller børneændring, så en
    MutationObserver på elementet ville aldrig opdage det. Vi prøver derfor
    igen et begrænset antal gange, mens vi står i nødtilfældet.

    Opretter appen aldrig en læsbar shadow root, fordi den er mode: closed,
    bliver nødtilfældet permanent. Det er med vilje: så kan vi ikke måle, og
    så er reserveret plads det rigtige valg.
  */
  function scheduleRetry() {
    if (!alive || retryTimer !== null || retriesLeft <= 0) return;
    retriesLeft -= 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (alive) measure();
    }, RETRY_INTERVAL);
  }

  function watchShadowRoot(shadowRoot) {
    if (shadowRoot === observedShadowRoot) return;
    if (shadowObserver) shadowObserver.disconnect();
    observedShadowRoot = shadowRoot;
    shadowObserver = null;
    if (!shadowRoot) return;
    shadowObserver = new MutationObserver(schedule);
    shadowObserver.observe(shadowRoot, { attributes: true, childList: true, subtree: true });
  }

  /*
    Vi genkender appens boks på dens geometri, ikke på dens klassenavn.
    Klassenavnene er appens interne CSS-modul-navne med skiftende hash, og de
    forsvinder også helt, når brugeren lukker fanen. Et navnematch gav derfor
    116 px reserveret plads i et tilfælde, hvor der intet var at gøre plads til.

    Vi tager heller ikke bare det første element. Lægger appen en dag et lag
    mere ind, ville vi ramme forkert og blive ved med det i stilhed. Vi leder
    i stedet efter alle faste elementer, der rent faktisk ligger nede ved
    bjælken. Der er kun to niveauer, fordi appens overlay ligger øverst, og en
    dyb gennemgang ville koste en stilberegning per element ved hver måling.
  */
  function findFixedElements(root, depth) {
    const found = [];

    for (const element of root.children) {
      if (getComputedStyle(element).position === 'fixed') {
        found.push(element);
      } else if (depth > 0) {
        found.push(...findFixedElements(element, depth - 1));
      }
    }

    return found;
  }

  function measure() {
    if (!alive || !bar.isConnected) return;

    const embed = document.querySelector('shopify-forms-embed');

    if (!embed) {
      watchShadowRoot(null);
      watchTeaser(null);
      write(null);
      return;
    }

    const shadowRoot = embed.shadowRoot;

    if (!shadowRoot) {
      watchShadowRoot(null);
      watchTeaser(null);
      write(FALLBACK_CLEARANCE);
      scheduleRetry();
      return;
    }

    watchShadowRoot(shadowRoot);

    const bottom = restingBottom();

    /*
      Kun barBox.left og barBox.right bruges. De påvirkes ikke af animationen,
      som kun flytter bjælken lodret.
    */
    const barBox = bar.getBoundingClientRect();

    let overlay = null;
    let clearance = 0;

    for (const candidate of findFixedElements(shadowRoot, 1)) {
      const box = candidate.getBoundingClientRect();

      if (box.width === 0 || box.height === 0) continue;

      // Modalen dækker alligevel hele skærmen, så der er intet at gøre plads til.
      if (coversViewport(candidate)) continue;

      // Kun noget, der faktisk ligger nede ved bjælken, kan dække knappen.
      if (box.bottom < bottom - BOTTOM_TOLERANCE) continue;

      if (!overlapsHorizontally(barBox, box)) continue;

      const needed = Math.max(0, Math.round(bottom - box.top));

      if (needed > clearance) {
        clearance = needed;
        overlay = candidate;
      }
    }

    watchTeaser(overlay);
    write(clearance);
  }

  function schedule() {
    if (!alive || frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      if (alive) measure();
    });
  }

  /*
    Appen indsættes sent og direkte i body, så vi behøver ikke lytte på hele
    dokumentets undertræ. Det ville betyde en måling for hver eneste DOM-
    ændring fra alle andre apps på siden.
  */
  const bodyObserver = new MutationObserver(schedule);
  bodyObserver.observe(document.body, { childList: true });

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });
  customElements.whenDefined('shopify-forms-embed').then(schedule, () => {});

  measure();

  return {
    bar,
    stop() {
      /*
        alive slås fra først. customElements.whenDefined kan nå at svare efter
        at sektionen er udskiftet, og uden flaget ville den kalde en måling på
        en bjælke, der ikke længere findes på siden.
      */
      alive = false;
      if (frame !== null) cancelAnimationFrame(frame);
      if (retryTimer !== null) clearTimeout(retryTimer);
      bodyObserver.disconnect();
      if (shadowObserver) shadowObserver.disconnect();
      if (teaserObserver) teaserObserver.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
      bar.style.removeProperty(CLEARANCE_PROPERTY);
    },
  };
}

function restart() {
  if (active) active.stop();
  active = start();
}

restart();

/*
  Temaeditoren udskifter sektionen uden at genindlæse siden. Modulet køres
  ikke igen af sig selv, så uden dette ville den nye bjælke stå uden frirum,
  mens observerne pegede på den gamle.
*/
document.addEventListener('shopify:section:load', (event) => {
  if (event.target && event.target.querySelector(BAR_SELECTOR)) restart();
});

/*
  Vi stopper kun, hvis det er vores egen bjælke, der forsvinder. En anden
  sektion, der fjernes, må ikke slå frirummet fra.
*/
document.addEventListener('shopify:section:unload', (event) => {
  if (!active || !event.target) return;
  if (event.target.contains(active.bar)) {
    active.stop();
    active = null;
  }
});
